import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, ArrowRight, LayoutDashboard, User, Bell, Search, Zap } from "lucide-react";
import { ProTrackMark, StackLogo } from "../components/ProTrackLogo";

const SETUP_STEPS = [
  {
    icon: User,
    title: "Complete your profile",
    desc: "Add your company's industry, preferred states, and contract value range. This powers AI matching.",
    path: "/profile",
    cta: "Set up profile",
    color: "#4f8ef7",
  },
  {
    icon: Search,
    title: "Browse & save tenders",
    desc: "Search all 38 sources. Save tenders you're interested in — they'll appear in your Deadline Pulse.",
    path: "/tenders",
    cta: "Browse tenders",
    color: "#00d4a1",
  },
  {
    icon: Zap,
    title: "Check AI Matched",
    desc: "Your AI matches are ready. ProTrackNG ranks the best-fit tenders for your company profile.",
    path: "/matched",
    cta: "View matches",
    color: "#a78bfa",
  },
  {
    icon: Bell,
    title: "Enable deadline alerts",
    desc: "Turn on WhatsApp and email alerts in Alerts. You'll be notified 30, 14, and 7 days before deadlines.",
    path: "/alerts",
    cta: "Set up alerts",
    color: "#ffd166",
  },
];

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const plan = params.get("plan") || "professional";
  const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
  const [currentStep, setCurrentStep] = useState(0);

  return (
    <div style={{ minHeight: "100vh", background: "#080d1a", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Serif+Display&display=swap');
        * { box-sizing: border-box; }
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .pop-in { animation: popIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275) forwards; }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
        .step-card { background: rgba(232,234,240,0.02); border: 1px solid rgba(232,234,240,0.07); border-radius: 14px; padding: 20px; cursor: pointer; transition: all 0.2s; }
        .step-card:hover { background: rgba(232,234,240,0.04); border-color: rgba(232,234,240,0.12); transform: translateY(-2px); }
        .step-card.active { border-color: rgba(0,212,161,0.3); background: rgba(0,212,161,0.04); }
      `}</style>

      <div style={{ width: "100%", maxWidth: "680px" }}>

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "48px", justifyContent: "center" }}>
          <ProTrackMark size={28} />
          <span style={{ fontSize: "16px", fontWeight: "800", color: "#fff" }}>ProTrack<span style={{ color: "#00d4a1" }}>NG</span></span>
        </div>

        {/* Success badge */}
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <div className="pop-in" style={{ width: "80px", height: "80px", borderRadius: "50%", background: "linear-gradient(135deg, rgba(0,212,161,0.2), rgba(0,212,161,0.08))", border: "2px solid rgba(0,212,161,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <CheckCircle size={40} color="#00d4a1" />
          </div>
          <div className="fade-up" style={{ animationDelay: "0.2s", opacity: 0 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "5px 16px", background: "rgba(0,212,161,0.1)", border: "1px solid rgba(0,212,161,0.25)", borderRadius: "100px", fontSize: "12px", fontWeight: "700", color: "#00d4a1", marginBottom: "16px", letterSpacing: "0.5px" }}>
              ✓ SUBSCRIPTION ACTIVE
            </div>
            <h1 style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: "900", color: "#fff", letterSpacing: "-1px", fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: "12px", lineHeight: "1.15" }}>
              Welcome to ProTrackNG<br /><span style={{ color: "#00d4a1" }}>{planName} Plan</span>
            </h1>
            <p style={{ fontSize: "15px", color: "rgba(232,234,240,0.5)", lineHeight: "1.7", maxWidth: "480px", margin: "0 auto 8px" }}>
              Your subscription is confirmed. A receipt has been sent to your email. Here's how to get started in 4 steps.
            </p>
          </div>
        </div>

        {/* Setup steps */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "14px", marginBottom: "32px" }}>
          {SETUP_STEPS.map((step, i) => {
            const Icon = step.icon;
            const done = currentStep > i;
            return (
              <div
                key={i}
                className={`step-card ${currentStep === i ? "active" : ""}`}
                onClick={() => { setCurrentStep(i); navigate(step.path); }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: done ? "rgba(0,212,161,0.15)" : `${step.color}15`, border: `1px solid ${done ? "rgba(0,212,161,0.3)" : step.color + "30"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {done ? <CheckCircle size={18} color="#00d4a1" /> : <Icon size={18} color={step.color} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                      <span style={{ fontSize: "10px", fontWeight: "700", color: "rgba(232,234,240,0.3)", letterSpacing: "0.5px" }}>STEP {i + 1}</span>
                      {done && <span style={{ fontSize: "10px", fontWeight: "700", color: "#00d4a1" }}>✓ DONE</span>}
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8eaf0", marginBottom: "4px" }}>{step.title}</div>
                    <div style={{ fontSize: "12px", color: "rgba(232,234,240,0.45)", lineHeight: "1.5", marginBottom: "10px" }}>{step.desc}</div>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: step.color, display: "flex", alignItems: "center", gap: "4px" }}>
                      {step.cta} <ArrowRight size={11} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main CTA */}
        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{ padding: "14px 40px", background: "linear-gradient(135deg, #00d4a1, #00b888)", border: "none", borderRadius: "12px", color: "#080d1a", fontSize: "15px", fontWeight: "700", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}
          >
            <LayoutDashboard size={16} /> Go to Dashboard
          </button>
          <div style={{ fontSize: "12px", color: "rgba(232,234,240,0.3)" }}>
            You can return to this guide any time from Support → Getting Started
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "48px", paddingTop: "24px", borderTop: "1px solid rgba(232,234,240,0.06)" }}>
          <span style={{ fontSize: "11px", color: "rgba(232,234,240,0.25)" }}>Powered by</span>
          <StackLogo size={14} variant="white" />
          <span style={{ fontSize: "11px", color: "rgba(232,234,240,0.3)", fontWeight: "600" }}>ProStack NG Technologies</span>
        </div>
      </div>
    </div>
  );
}
